import React from 'react';

import { useTranslator } from '../../../components/i18n/useTranslator';
import ToggleChip from '../behaviours/ToggleChip';
import AssignmentChipView from '../chips/AssignmentChipView';
import TaskItemAssignmentPopupContent from '../chips/TaskItemAssignmentPopupContent';
import { useAssignmentTaskItemChipCallbacks } from '../hooks/useAssignmentTaskItemChipCallbacks';
import { replaceAssignmentList } from '../../../../common/tiptap/extensions/taskList/taskListItemAssignmentUtils';
import type { ElementAssignmentChipDescriptor, ElementAssignmentPlaceholderChipDescriptor } from '../elementChipsTypes';
import type { TaskItemAssignment } from '../../../../common/tiptap/extensions/taskList/taskListItemChipTypes';
import type { UpdateTaskItemAttrs } from '../taskItemChipsUtils';
import { ChipBaseProps } from '../../../../common/@roma/src/components/base/chip/ChipBase';

type TaskItemAssignmentChipProps = {
    chip: ElementAssignmentChipDescriptor | ElementAssignmentPlaceholderChipDescriptor;
    size: ChipBaseProps['size'];
    assignments: TaskItemAssignment[];
    updateAttributes: UpdateTaskItemAttrs;
    canModify: boolean;
};

/**
 * Assignment (or placeholder) chip for a Tiptap task item. The picker's writes
 * go through the owning node's `updateAttributes` (like the other task-item
 * chips) rather than the element-id popup the element chip uses: picking a
 * user replaces the current assignee (a task has a single assignee),
 * deleting/clearing empties the assignees and turns off the slot.
 *
 * The placeholder and assigned states render through one component (and one
 * `ToggleChip`, keyed by the stable `assignment` chip id) so that assigning a
 * user doesn't remount the chip — which would drop its selection. The picker
 * therefore closes but the chip stays selected after assigning.
 */
const TaskItemAssignmentChip: React.FC<TaskItemAssignmentChipProps> = ({
    chip,
    size,
    assignments,
    updateAttributes,
    canModify,
}) => {
    const translate = useTranslator('elements', 'elementTypes.task');
    const { onRemove } = useAssignmentTaskItemChipCallbacks({
        updateAttributes,
        canModifyAssignments: canModify,
    });

    const renderPopupContent = ({ closePopup }: { closePopup: () => void }) => (
        <TaskItemAssignmentPopupContent
            assignments={assignments}
            onAssign={({ id, name }) =>
                updateAttributes({
                    assignments: replaceAssignmentList(assignments, { assigneeId: id, assigneeName: name }),
                    showAssignments: true,
                })
            }
            onRemoveAll={onRemove}
            closePopup={closePopup}
        />
    );

    const isAssigned = chip.type === 'assignment';
    const label = isAssigned ? `@${chip.assigneeName}` : translate('assignment.placeholder');

    return (
        <ToggleChip chip={chip} canModify={canModify} onDelete={onRemove}>
            {({ isSelected, isPopupOpen, closePopup, canActivate, deselect, chipRef, chipProps }) => (
                <AssignmentChipView
                    chipRef={chipRef}
                    chipProps={chipProps}
                    size={size}
                    variant={isAssigned ? 'user' : 'normal'}
                    containerId={chip.containerId}
                    isSelected={isSelected}
                    isPopupOpen={isPopupOpen}
                    isDisabled={!canActivate}
                    isSearchHighlighted={chip.isSearchHighlighted}
                    deselect={deselect}
                    closePopup={closePopup}
                    renderPopupContent={renderPopupContent}
                >
                    {label}
                </AssignmentChipView>
            )}
        </ToggleChip>
    );
};

export default TaskItemAssignmentChip;
